
































































































import LazyHydrate from 'vue-lazy-hydration';
import CustomHeading from '~/components/General/CustomHeading.vue';
import { SfSelect, SfLoader } from '@storefront-ui/vue';
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  ssrRef,
  useFetch,
  useContext,
  useRoute,
} from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import SkeletonLoader from '~/components/SkeletonLoader/index.vue';
import CategoryPagination from '~/modules/catalog/category/components/pagination/CategoryPagination.vue';
import { useFacet, useUiHelpers, useUiState, useApi, useUser } from '~/composables';

import { useAddToCart } from '~/helpers/cart/addToCart';
import { useUrlResolver } from '~/composables/useUrlResolver';
import { useWishlist } from '~/modules/wishlist/composables/useWishlist';
import { usePrice } from '~/modules/catalog/pricing/usePrice';
import { useCategoryContent } from '~/modules/catalog/category/components/cms/useCategoryContent';
import { useTraverseCategory } from '~/modules/catalog/category/helpers/useTraverseCategory';
import facetGetters from '~/modules/catalog/category/getters/facetGetters';

import CategoryNavbar from '~/modules/catalog/category/components/navbar/CategoryNavbar.vue';
import CategoryBreadcrumbs from '~/modules/catalog/category/components/breadcrumbs/CategoryBreadcrumbs.vue';
import { isCategoryTreeRoute } from '~/modules/GraphQL/CategoryTreeRouteTypeguard';

import type { ProductInterface, CategoryTree } from '~/modules/GraphQL/types';
import type { SortingModel } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import type { Pagination } from '~/composables/types';
import type { Product } from '~/modules/catalog/product/types';

import { useUrlData } from '~/stores/urldata';

import { createProductAttributeFilterInput } from '~/modules/catalog/category/composables/useFacet/input/createProductAttributeFilterInput';
import getFilterPagesSeoDescDataGql from '~/customQueries/metaQueries/getFilterPagesSeoDescData.gql';
import getFilterPagesSeoTitleDataGql from '~/customQueries/metaQueries/getFilterPagesSeoTitleData.gql';

export default defineComponent({
  name: 'CategoryPage',
  components: {
    CategoryPagination,
    CategoryEmptyResults: () => import('~/modules/catalog/category/components/CategoryEmptyResults.vue'),
    CategoryFilters: () => import('~/modules/catalog/category/components/filters/CategoryFilters.vue'),
    CmsContent: () => import('~/modules/catalog/category/components/cms/CmsContent.vue'),
    CategoryProductGrid: () => import('~/modules/catalog/category/components/views/CategoryProductGrid.vue'),
    CategoryProductList: () => import('~/modules/catalog/category/components/views/CategoryProductList.vue'),
    CategoryNavbar,
    CategoryBreadcrumbs,
    SfSelect,
    LazyHydrate,
    CustomHeading,
    SkeletonLoader,
    SfLoader,
  },
  setup() {
    const urlData = useUrlData();
    const { query } = useApi();
    const { app } = useContext();
    const route = useRoute();
    const { getContentData } = useCategoryContent();
    const { addTags } = useCache();
    const { isAuthenticated } = useUser();
    const uiHelpers = useUiHelpers();
    const cmsContent = ref('');
    const cmsPageBuilderContent = ref('');
    const isShowCms = ref(false);
    const loadMorePage = ref();
    const isShowProducts = ref(false);
    const products = ssrRef<ProductInterface[]>([]);
    const sortBy = ref<SortingModel>({ selected: '', options: [] });
    const pagination = ref<Pagination>({});

    const productContainerElement = ref<HTMLElement | null>(null);

    const {
      toggleFilterSidebar,
      changeToCategoryListView,
      changeToCategoryGridView,
      isCategoryGridView,
      isFilterSidebarOpen,
    } = useUiState();
    const { addItem: addItemToWishlistBase, isInWishlist, removeItem: removeItemFromWishlist } = useWishlist();
    const { result, search } = useFacet();
    const { addItemToCart } = useAddToCart();

    const addItemToWishlist = async (product: Product) => {
      await (isInWishlist({ product }) ? removeItemFromWishlist({ product }) : addItemToWishlistBase({ product }));
    };

    const { activeCategory, loadCategoryTree } = useTraverseCategory();
    const activeCategoryName = ref('');
    const categoryDescription = ref('');
    const catIntroData = ref('');
    const categoryImage = ref('');
    const routeData = ref<CategoryTree | null>(null);
    const mainCategoryUid = ref(null);

    const filterSeoText = ref('');
    const firstLoaded = ref(false);
    const filterSeoTitle = ref('');

    const { fetch } = useFetch(async () => {
      initializeLoadMorePage();

      if (!activeCategory.value) {
        loadCategoryTree();
      }

      const resolvedUrl = urlData.resolverUrl;

      if (isCategoryTreeRoute(resolvedUrl)) routeData.value = resolvedUrl;

      const categoryUid = routeData.value?.uid;

      mainCategoryUid.value = routeData.value?.uid || null;
      let stockStatusFilter = { stock_status: { is_in_stock: true } };
      let facetsObj = { ...uiHelpers.getFacetsFromURL(), category_uid: categoryUid };
      facetsObj.itemsPerPage = 12;
      facetsObj.page = loadMorePage.value;
      let filterObjNew = Object.assign(facetsObj.filters, stockStatusFilter);
      facetsObj.filters = filterObjNew;

      const [content] = await Promise.all([getContentData(routeData.value?.uid), search(facetsObj)]);

      cmsContent.value = content?.cmsBlock?.content ?? '';

      isShowCms.value = content.isShowCms;
      isShowProducts.value = content.isShowProducts;
      activeCategoryName.value = content.categoryName ?? '';

      const catRawDescData = content.categoryDescription;
      const catRawIntroData = content.categoryIntroduction;

      try {
        if (catRawDescData) {
          try {
            categoryDescription.value = JSON.parse(catRawDescData);
          } catch (e) {
            // Use fallback in non-JSON format
            categoryDescription.value = catRawDescData;
          }
        }
        if (catRawIntroData) {
          try {
            catIntroData.value = JSON.parse(catRawIntroData);
          } catch (e) {
            // Use fallback in non-JSON format
            catIntroData.value = catRawIntroData;
          }
        }

        // @ts-ignore
        let pageBuilderRaw = content?.cmsBlock?.page_builder_content ?? '';
        if (pageBuilderRaw) {
          cmsPageBuilderContent.value = JSON.parse(pageBuilderRaw);
        }
      } catch (e) {
        console.log('Parsing error: ', e);
      }
      categoryImage.value = content.categoryImage;
      const additionalProducts = facetGetters.getProducts(result.value) ?? [];

      products.value = [...products.value, ...additionalProducts];
      app.$gtm.push({ ecommerce: null });
      app.$gtm.push({
        event: 'view_item_list',
        ecommerce: {
          item_list_id: activeCategoryName.value.replace(' ', '_').toLowerCase(),
          item_list_name: activeCategoryName.value,
          items: products.value.map((item, index) => {
            return {
              item_id: item.sku,
              item_name: item.name,
              index: index + 1,
              ...item.categories.reduce((acc, category, index) => {
                acc[`item_category${index + 1}`] = category.name;
                return acc;
              }, {}),
              item_list_id: activeCategoryName.value.replace(' ', '_').toLowerCase(),
              item_list_name: activeCategoryName.value,
              price: item.price_range.minimum_price.regular_price.value,
              discount:
                item.price_range.minimum_price.final_price.value !== item.price_range.minimum_price.regular_price.value
                  ? item.price_range.minimum_price.final_price.value
                  : '',
              currency: item.price_range.minimum_price.final_price.currency,
              item_brand: item.custom_attributes?.find((attr) => attr.attribute_metadata.code === 'manufacturer')
                ?.selected_attribute_options?.attribute_option[0]?.label,
            };
          }),
        },
      });

      sortBy.value = facetGetters.getSortOptions(result.value);
      pagination.value = facetGetters.getPagination(result.value);

      const tags = [{ prefix: CacheTagPrefix.View, value: 'category' }];
      const productTags = products.value.map((product) => ({
        prefix: CacheTagPrefix.Product,
        value: product.uid,
      }));

      await filterSeoData(categoryUid);

      if (!isAuthenticated.value) {
        addTags([...tags, ...productTags]);
      }

      if (!firstLoaded.value) firstLoaded.value = true;
    });

    const isPriceLoaded = ref(false);
    onMounted(async () => {
      const { getPricesBySku } = usePrice();
      if (products.value.length > 0) {
        const skus = products.value.map((item) => item.sku);
        const priceData = await getPricesBySku(skus, pagination.value.itemsPerPage);
        products.value = products.value.map((product) => ({
          ...product,
          price_range: priceData.items.find((item) => item.sku === product.sku)?.price_range,
        }));
      }
      isPriceLoaded.value = true;

      // Scroll to top
      window.scrollTo(0, 0);
    });

    const goToPage = (page: number) => {
      // uiHelpers.changePage(page, false);
      if (page == 0) {
        loadMorePage.value = 1;
        products.value = [];
      }
      fetch();
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      goToPage(0);
    };

    const onReloadProducts = () => {
      goToPage(0);
      productContainerElement.value.scrollIntoView();
    };

    const filterSeoData = async (catUid) => {
      try {
        filterSeoText.value = '';
        filterSeoTitle.value = '';

        let facetsObj = {
          ...uiHelpers.getFacetsFromURL(),
          category_uid: catUid,
        };

        let appliedFilters = createProductAttributeFilterInput(facetsObj);

        const filterObjNew = Object.entries(appliedFilters).reduce((obj, [key, value]) => {
          obj[key] = value;
          return obj;
        }, {});

        let filterObj = {
          filters: filterObjNew,
        };

        const { data, errors } = await query(getFilterPagesSeoDescDataGql, filterObj);
        const { data: title, errors: titleErrors } = await query(getFilterPagesSeoTitleDataGql, filterObj);
        if (errors) {
          const joinedErrors = errors.map((e) => e.message).join(',');
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          throw new Error(joinedErrors);
        }
        if (titleErrors) {
          const joinedTitleErrors = titleErrors.map((e) => e.message).join(',');
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          throw new Error(joinedTitleErrors);
        }

        // @ts-ignore
        filterSeoText.value = data?.products?.mw_seo_category_data?.description || '';
        // @ts-ignore
        // prettier-ignore
        filterSeoTitle.value = title?.products?.mw_seo_category_data?.category_seo_name || title?.products?.mw_seo_category_data?.name || '';
      } catch (e) {
        console.log('Errors: ', e);
      }
    };

    const loadMore = (e) => {
      let cp = pagination.value.currentPage;
      let np = cp + 1;
      let tp = pagination.value.totalPages;

      if (np <= tp) {
        loadMorePage.value = np;
        goToPage(np);
      }
    };

    const initializeLoadMorePage = () => {
      if (loadMorePage.value) return;

      const pageFromQuery = route.value.query.page;
      loadMorePage.value = pageFromQuery ? parseInt(pageFromQuery.toString(), 10) : 1;
    };

    return {
      categoryImage,
      categoryDescription,
      catIntroData,
      isPriceLoaded,
      ...uiHelpers,
      toggleFilterSidebar,
      isCategoryGridView,
      changeToCategoryListView,
      changeToCategoryGridView,
      isFilterSidebarOpen,
      addItemToCart,
      addItemToWishlist,
      pagination,
      products,
      sortBy,
      isShowCms,
      isShowProducts,
      cmsContent,
      activeCategoryName,
      routeData,
      doChangeItemsPerPage,
      productContainerElement,
      onReloadProducts,
      goToPage,
      loadMore,
      loadMorePage,

      cmsPageBuilderContent,

      filterSeoText,
      firstLoaded,
      filterSeoTitle,
    };
  },
});
